/**
 * PATH: apps/billing/src/pages/BillDetailPage.tsx
 * FILE_ID: BILLING-PAGE-BILLDETAIL-001
 * PURPOSE: Detailed view of a single bill with approve / reject / push-to-Xero actions.
 * WHY: BillsPage.tsx links to /bills/{id}; this page used to render a static
 *      mock. Now wired live to GET /api/bills/{id}/ via useBill().
 *
 * FLOW:
 *   useParams.id -> useBill(id) -> BillDetail
 *     header (vendor + invoice #)
 *     summary card (dates + totals)
 *     line items table
 *     sidebar:
 *       AI confidence + extraction error (if any)
 *       Xero sync status + push button
 *
 * DEPENDENCIES:
 *   ../hooks/useBills: useBill, useApproveBill, useRejectBill, useXeroPushBill
 *
 * RELATED:
 *   docs/architecture/billing/00_MASTER_PLAN.md § Phase A
 */

import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Send,
  AlertTriangle,
  Edit,
  ExternalLink,
  Loader2,
  XCircle,
  Clock,
} from 'lucide-react';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import {
  useBill,
  useApproveBill,
  useRejectBill,
  useXeroPushBill,
  type BillStatus,
  type BillLineItem,
} from '../hooks/useBills';

// ---------------------------------------------------------------------------
// Status display config — same shape as BillsPage so visual language matches.
// ---------------------------------------------------------------------------

const statusConfig: Record<
  BillStatus,
  { label: string; color: string; icon: typeof FileText }
> = {
  pending: {
    label: 'Pending extraction',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    icon: FileText,
  },
  extracted: {
    label: 'AI extracted',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    icon: Clock,
  },
  reviewed: {
    label: 'Reviewed',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    icon: CheckCircle,
  },
  pushed: {
    label: 'Pushed to Xero',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    icon: Send,
  },
  failed: {
    label: 'Extraction failed',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    icon: AlertTriangle,
  },
};

function parseAmount(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function lineItemUnitPrice(item: BillLineItem): number | null {
  const v = item.unit_amount;
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function lineItemTotal(item: BillLineItem): number | null {
  const v = item.line_total;
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: bill, isLoading, error, refetch } = useBill(id);

  const approve = useApproveBill();
  const reject = useRejectBill();
  const xeroPush = useXeroPushBill();

  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px] text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading bill…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">
            Couldn't load bill: {error.message}
          </p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => refetch()}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
            <Link
              to="/bills"
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Back to bills
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">Bill not found.</p>
        <Link to="/bills" className="text-sm text-emerald-600 hover:text-emerald-700">
          Back to bills
        </Link>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const statusInfo = statusConfig[bill.status];
  const StatusIcon = statusInfo?.icon ?? FileText;
  const total = parseAmount(bill.total);
  const subtotal = parseAmount(bill.subtotal);
  const tax = parseAmount(bill.gst);
  const confidencePct = bill.extraction_confidence
    ? Math.round(parseFloat(bill.extraction_confidence) * 100)
    : null;

  const canApprove = bill.status === 'extracted' || bill.status === 'failed';
  const canReject = bill.status === 'extracted' || bill.status === 'pending';
  const canXeroPush = bill.status === 'reviewed' && !bill.xero_invoice_id;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link
          to="/bills"
          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Bills
        </Link>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">
          {bill.invoice_number || bill.id.slice(0, 8)}
        </span>
      </div>

      {/* Header + actions */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {bill.vendor_name || bill.email_from || '(unknown vendor)'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {bill.invoice_number ? `Invoice #${bill.invoice_number}` : 'No invoice number extracted'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1',
              statusInfo?.color,
            )}
          >
            <StatusIcon className="h-4 w-4" />
            {statusInfo?.label ?? bill.status}
          </span>
          {canApprove && (
            <button
              onClick={() => approve.mutate({ billId: bill.id })}
              disabled={approve.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {approve.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Approve
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setShowRejectForm((v) => !v)}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          )}
          {canXeroPush && (
            <button
              onClick={() => xeroPush.mutate({ billId: bill.id })}
              disabled={xeroPush.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {xeroPush.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Push to Xero
            </button>
          )}
          <Link
            to={`/bills/${bill.id}?edit=1`}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </div>
      </div>

      {/* Reject form (inline, only when toggled) */}
      {showRejectForm && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 space-y-3">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            Reject this bill? Optionally include a reason.
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. duplicate of bill #INV-123"
            className="w-full p-2 text-sm border border-red-300 dark:border-red-700 rounded bg-white dark:bg-gray-800"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                reject.mutate(
                  { billId: bill.id, reason: rejectReason },
                  {
                    onSuccess: () => {
                      setShowRejectForm(false);
                      setRejectReason('');
                    },
                  },
                );
              }}
              disabled={reject.isPending}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
            </button>
            <button
              onClick={() => setShowRejectForm(false)}
              className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action result banners */}
      {approve.isError && (
        <ActionErrorBanner action="Approve" message={approve.error?.message} />
      )}
      {reject.isError && (
        <ActionErrorBanner action="Reject" message={reject.error?.message} />
      )}
      {xeroPush.isError && (
        <ActionErrorBanner action="Xero push" message={xeroPush.error?.message} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Invoice summary
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <DetailRow label="Invoice date" value={bill.invoice_date ? formatDate(bill.invoice_date) : '—'} />
              <DetailRow label="Due date" value={bill.due_date ? formatDate(bill.due_date) : '—'} />
              <DetailRow
                label="Subtotal"
                value={subtotal != null ? formatCurrency(subtotal) : '—'}
              />
              <DetailRow
                label="GST"
                value={tax != null ? formatCurrency(tax) : '—'}
              />
              {bill.reference && <DetailRow label="Reference" value={bill.reference} />}
              {bill.currency && bill.currency !== 'AUD' && (
                <DetailRow label="Currency" value={bill.currency} />
              )}
              <div className="col-span-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total amount</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {total != null ? formatCurrency(total) : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Line items */}
          {bill.line_items?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Line items ({bill.line_items.length})
                </h2>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      Qty
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      Unit price
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {bill.line_items.map((item, idx) => {
                    const unit = lineItemUnitPrice(item);
                    const lineTotal = lineItemTotal(item);
                    return (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-gray-900 dark:text-white">
                          {item.description ?? '(no description)'}
                          {item.tax_type && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              {item.tax_type}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                          {item.quantity ?? '—'}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">
                          {unit != null ? formatCurrency(unit) : '—'}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                          {lineTotal != null ? formatCurrency(lineTotal) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI confidence + extraction error */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">AI extraction</h3>
            <div className="space-y-4">
              {confidencePct != null ? (
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Confidence</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {confidencePct}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        confidencePct >= 90
                          ? 'bg-emerald-500'
                          : confidencePct >= 70
                            ? 'bg-amber-500'
                            : 'bg-red-500',
                      )}
                      style={{ width: `${confidencePct}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No extraction confidence yet.</p>
              )}
              {bill.extraction_error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-800 dark:text-red-200">
                    {bill.extraction_error}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Source email */}
          {bill.email_id && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Source email</h3>
              <div className="space-y-3">
                <DetailRow label="From" value={bill.email_from_name || bill.email_from} />
                <DetailRow label="Subject" value={bill.email_subject} />
                <DetailRow
                  label="Received"
                  value={bill.email_received_at ? formatDate(bill.email_received_at) : '—'}
                />
                {bill.attachment_filename && (
                  <DetailRow label="Attachment" value={bill.attachment_filename} />
                )}
              </div>
            </div>
          )}

          {/* Xero sync */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Xero</h3>
            <div className="space-y-3">
              {bill.xero_invoice_id ? (
                <>
                  <DetailRow
                    label="Status"
                    value={<span className="text-emerald-600 font-medium">Pushed</span>}
                  />
                  <DetailRow label="Xero invoice #" value={bill.xero_invoice_number || '—'} />
                  {bill.xero_pushed_at && (
                    <DetailRow label="Pushed at" value={formatDate(bill.xero_pushed_at)} />
                  )}
                  <button
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View in Xero
                  </button>
                </>
              ) : bill.xero_push_error ? (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800 dark:text-red-200">
                    Push failed: {bill.xero_push_error}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {bill.status === 'reviewed'
                    ? 'Ready to push.'
                    : 'Approve the bill first to push to Xero.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="font-medium text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function ActionErrorBanner({ action, message }: { action: string; message?: string }) {
  return (
    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
      {action} failed: {message ?? 'Unknown error'}
    </div>
  );
}
